<template>
  <v-menu
    v-if="!filterable"
    ref="menu"
    v-model="isOpened"
    content-class="zoox-component zoox-menu-chip-date mt-2"
    offset-y
    max-width="290px"
    offset-overflow
    v-bind="$attrs"
    :left="left"
    :right="right"
    :top="top"
    :bottom="bottom"
    :close-on-content-click="false"
    :return-value="value"
  >
    <template #activator="{on, attrs}">
      <v-chip
        class="zoox-component zoox-chip zoox-chip-date with-icon"
        label
        tabindex="0"
        :disabled="disabled"
        :color="appliedColor"
        :dark="dark"
        :class="chipClasses"
        v-bind="attrs"
        v-on="on"
      >
        <z-icon class="mr-1" :size="iconSize" :color="appliedIconColor">
          {{ dateIcon }}
        </z-icon>

        <span
          class="zoox-chip-text"
          :class="{small, 'x-small': xSmall}"
          :style="{color: selected ? $zoox.colors.white : appliedColor}"
        >
          {{ formattedDate || text }}
        </span>
      </v-chip>
    </template>

    <v-date-picker
      no-title
      show-adjacent-months
      scrollabe
      color="primary"
      first-day-of-week="1"
      :picker-date.sync="pickerDate"
      :next-icon="nextIcon"
      :prev-icon="prevIcon"
      :range="range"
      :min="min"
      :max="max"
      :value="dateValue"
      @input="onChangeDate"
    >
      <template v-if="!eager">
        <v-spacer></v-spacer>

        <z-btn
          ref="cancel"
          :color="cancelBtnColor"
          :text="$t('cancel')"
          @click="onCancel"
        />

        <z-btn
          ref="ok"
          primary
          :disabled="range && dateValue.length < 2"
          :color="confirmBtnColor"
          :text="$t('ok')"
          @click="onConfirm"
        />
      </template>
    </v-date-picker>
  </v-menu>

  <filterable-chip-date
    v-else
    :color="color"
    :top="top"
    :bottom="bottom"
    :left="left"
    :right="right"
    :value="value"
    :dark="dark"
    :selected="selected"
    :disabled="disabled"
    :small="small"
    :x-small="xSmall"
    :language-format="format"
    :cancel-btn-color="cancelBtnColor"
    :confirm-btn-color="confirmBtnColor"
    @input="$emit('input', $event)"
  >
  </filterable-chip-date>
</template>

<script>
import chipsMixin from '../chipsMixin'
import FilterableChipDate from './private/FilterableChipDate.vue'
import {compareAsc, parseISO, format} from 'date-fns'

/**
 * @displayName Chip Date
 */
export default {
  name: 'ZChipDate',
  components: {FilterableChipDate},
  mixins: [chipsMixin],
  props: {
    /**
     * Flag para indicar que se quer um range de datas
     */
    range: {
      type: Boolean,
      default: false,
    },
    /**
     * Valor do picker de data <br /> <br />
     *
     * Seu valor deve ser no formato `ISO 8601` `(YYYY-MM-DD)`
     */
    value: {
      type: [String, Array],
    },
    /**
     * Formato de exibição de data
     *
     * @values pt-BR, en-US, es-ES
     */
    format: {
      type: String,
      default: 'pt-BR',
    },
    /**
     * Flag para indicar que a mudança de valor é feita
     * no momento do click na data e não na confirmação
     * do botão.
     */
    eager: {
      type: Boolean,
      default: false,
    },

    /**
     * Define o ícone aplicado ao chip-date
     */
    dateIcon: {
      type: String,
      default: '$date_range_rounded',
    },
    /**
     * Define a cor do botão de cancelar
     */
    cancelBtnColor: {
      type: String,
      default: 'primary',
    },
    /**
     * Define a cor do botão de confirmação
     */
    confirmBtnColor: {
      type: String,
      default: 'primary',
    },
    /**
     * Define o ícone de avançar entre as datas
     */
    nextIcon: {
      type: String,
      default: '$keyboard_arrow_right_rounded',
    },
    /**
     * Define o ícone de voltar entre as datas
     */
    prevIcon: {
      type: String,
      default: '$keyboard_arrow_left_rounded',
    },
    /**
     * Data mínima permitida (padrão ISO 8601 dd)
     */
    min: {
      type: String,
    },
    /**
     * Data máxima permitida (padrão ISO 8601 dd)
     */
    max: {
      type: String,
    },
    /**
     * Alinha o date-picker à direita do chip
     */
    right: {
      type: Boolean,
      default: false,
    },
    /**
     * Alinha o date-picker à esquerda do chip
     */
    left: {
      type: Boolean,
      default: false,
    },
    /**
     * Alinha o date-picker acima do chip
     */
    top: {
      type: Boolean,
      default: false,
    },
    /**
     * Alinha o date-picker abaixo do chip
     */
    bottom: {
      type: Boolean,
      default: false,
    },
    /**
     * Aplica funcionalidade de filtro de datas
     */
    filterable: {
      type: Boolean,
      default: false,
    },
  },
  data(vm) {
    return {
      isOpened: false,
      dateValue: vm.range ? [] : null,
      pickerDate: undefined,
    }
  },

  computed: {
    formattedDate() {
      if (!this.value || !this.value.length) return null

      if (!this.range) {
        switch (this.format) {
          case 'pt-BR':
          case 'es-ES':
            return format(parseISO(this.value), 'dd/MM/yyyy')
          case 'en-US':
            return format(parseISO(this.value), 'MM/dd/yyyy')
          default:
            return format(parseISO(this.value), 'dd/MM/yyyy')
        }
      } else if (this.value.length >= 2) {
        const [start, end] = this.value
          .map((date) => parseISO(date))
          .sort(compareAsc)

        switch (this.format) {
          case 'pt-BR':
          case 'es-ES':
            return `${format(start, 'dd/MM/yyyy')} - ${format(
              end,
              'dd/MM/yyyy'
            )}`
          case 'en-US':
            return `${format(start, 'MM/dd/yyyy')} - ${format(
              end,
              'MM/dd/yyyy'
            )}`
          default:
            return `${format(start, 'dd/MM/yyyy')} - ${format(
              end,
              'dd/MM/yyyy'
            )}`
        }
      }
      return ''
    },
  },
  watch: {
    value(newValue) {
      this.dateValue = newValue
    },

    isOpened() {
      //ao fechar o menu, se não tiver selecionado nada, volta o valor anterior

      this.dateValue = this.value
      const currentValue = this.range ? this.value[0] : this.value
      const year = currentValue ? parseISO(currentValue).getFullYear() : null
      const month = currentValue ? parseISO(currentValue).getMonth() + 1 : null

      if (year && month) {
        this.pickerDate = `${year}-${month}`
      }
    },
  },

  methods: {
    onChangeDate(newDate) {
      this.dateValue = newDate

      if (this.eager) {
        if (!this.range) {
          this.$emit('input', this.dateValue)
          this.isOpened = false
        } else if (this.dateValue.length === 2) {
          const sortedDates = this.dateValue
            .map((date) => parseISO(date))
            .sort(compareAsc)

          this.$emit(
            'input',
            sortedDates.map((d) => format(d, 'yyyy-MM-dd'))
          )
          this.isOpened = false
        }
      }
    },

    onConfirm() {
      /**
       * Evento emitido quando o usuário confirma a seleção
       *
       * @property {String} dateValue - Valor da data selecionada
       */

      if (this.range) {
        const sortedDates = this.dateValue
          .map((date) => parseISO(date))
          .sort(compareAsc)

        const formattedRangeDates = sortedDates.map((d) =>
          format(d, 'yyyy-MM-dd')
        )

        this.$emit('input', formattedRangeDates)
      } else {
        this.$emit('input', this.dateValue)
      }

      this.isOpened = false
    },
    onCancel() {
      if (this.range) {
        this.dateValue = []
      }

      this.isOpened = false
    },
  },
  i18n: {
    en: {
      ok: 'Ok',
      cancel: 'Cancel',
    },
    es: {
      ok: 'Ok',
      cancel: 'Cancelar',
    },
    pt: {
      ok: 'Ok',
      cancel: 'Cancelar',
    },
    fr: {
      ok: "D'accord",
      cancel: 'Annuler',
    },
    tr: {
      ok: 'Tamam',
      cancel: 'İptal',
    },
  },
}
</script>

<style lang="scss" scoped>
@use '@scss/chips' as c;

.zoox-chip-date {
  @include c.zoox-chip-base;
}

.zoox-menu-chip-date {
  border-radius: var(--component-card-radius);
  box-shadow: var(--zoox-card-shadow);
}
</style>
